import {
  ModuleRegistry,
  provideGlobalGridOptions,
  createGrid,
  themeBalham,
} from "ag-grid-community"

import { AllEnterpriseModule, LicenseManager } from "ag-grid-enterprise"

ModuleRegistry.registerModules([AllEnterpriseModule])

// Mark all grids as using legacy themes
provideGlobalGridOptions({ theme: "legacy" })

import moment from "moment"
import flatpickr from "flatpickr"
import flatpickrL10ns from "flatpickr/dist/l10n/index.js"
import tippy from "tippy.js"
import { hideAll } from "tippy.js"

import { GenericConfirmDialog } from "./generic_confirm_dialog_hook"
import { markAgGridDatePickerPopup } from "./date_picker_helpers"

// window.I18n is serialized once on the static render so it goes stale after a
// language change; refreshI18n swaps in the grid's connected-render data-i18n
// map at mount, falling back to window.I18n.
let I18n = window.I18n || {}

function refreshI18n(el) {
  const raw = el?.dataset?.i18n
  if (!raw) return
  try {
    I18n = JSON.parse(raw)
    // Drop the memoized date formatter so it rebuilds with the refreshed locale.
    shortDateFormatter = null
  } catch (error) {
    console.warn("[GRID] Failed to parse data-i18n; using window.I18n", error)
  }
}

// Get locale from server-provided I18n config
function getLocale() {
  return I18n?.numberFormat?.locale || "en"
}

function flatpickrLocale() {
  const code = String(getLocale()).toLowerCase().split(/[-_]/)[0]
  return flatpickrL10ns[code] || flatpickrL10ns["default"]
}

// Lazy-initialized date formatter for short dates
let shortDateFormatter = null

function getShortDateFormatter() {
  if (!shortDateFormatter) {
    shortDateFormatter = new Intl.DateTimeFormat(getLocale(), {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    })
  }
  return shortDateFormatter
}

function pushEvent(hook, event, params) {
  const metadata = JSON.parse(hook.el.dataset.metadata) || {}
  const sendEventsToSelf = metadata.sendEventsToSelf

  if (sendEventsToSelf) {
    hook.pushEventTo(hook.el, event, params)
  } else {
    hook.pushEvent(event, params)
  }
}

function columnStateForJsonOrObject(jsonOrObject) {
  if (jsonOrObject.state === undefined) {
    return JSON.parse(jsonOrObject)
  }

  return jsonOrObject.state
}

function applyColumnState(jsonOrObject, api) {
  if (!jsonOrObject || jsonOrObject == []) return

  let columnState = columnStateForJsonOrObject(jsonOrObject)
  if (!columnState) return

  // applyOrder: true handles column ordering without replacing column defs.
  // Replacing column defs via setGridOption resets AG Grid's filter model on every
  // LiveView re-render, which clears active column filters and corrupts selection.
  api.applyColumnState({ state: columnState, applyOrder: true })
}

function showProspects() {
  const currentUrl = new URL(window.location.href)
  return currentUrl.searchParams.get("show") == "prospects"
}

// Built at call time (not import) so it picks up the refreshed I18n.
function buildLocationColumnDefs() {
  return [
    {
      field: "street",
      headerName: I18n["Street Address"],
    },
    {
      field: "city",
      headerName: I18n["City"],
    },
    {
      field: "state",
      headerName: I18n["State"],
    },
    {
      field: "size",
      headerName: I18n["Size"],
    },
    {
      field: "expires_at",
      headerName: I18n["Lease Expiration Date"],
      cellRenderer: (params) => {
        if (!params.value) return

        if (params.data.expired) {
          return `<span style="color: red;">${params.value}</span>`
        } else if (params.data.expires_soon) {
          return `<span style="color: darkorange;">${params.value}</span>`
        } else {
          return `<span>${params.value}</span>`
        }
      },
    },
    {
      field: "product_type",
      headerName: I18n["Product Type"],
    },
    {
      field: "ownership_status",
      headerName: I18n["Status"],
    },
    {
      field: "notes",
      headerName: I18n["Notes"],
      wrapText: true,
      autoHeight: true,
    },
  ]
}

class CheckboxCellRenderer {
  constructor() {
    this.isOpen = false
  }

  init(params) {
    this.params = params
    this.isOpen = params.value
    this.eGui = document.createElement("div")
    this.eGui.classList.add(
      "ag-wrapper",
      "ag-input-wrapper",
      "ag-checkbox-input-wrapper",
      "ag-checked",
    )

    this.eGui.onclick = (event) => {
      event.preventDefault()
      event.stopPropagation()

      this.toggleCheckbox()
    }

    const checkbox = document.createElement("input")
    checkbox.type = "checkbox"
    checkbox.classList.add("ag-input-field-input", "ag-checkbox-input")
    checkbox.value = this.isOpen
    checkbox.onclick = (event) => {
      event.stopPropagation()
      event.preventDefault()
      this.toggleCheckbox()
    }

    this.eGui.appendChild(checkbox)
  }

  toggleCheckbox() {
    this.isOpen = !this.isOpen
    this.eGui.classList.toggle("ag-checked", this.isOpen)
    this.params.pushEvent("checked", {
      value: this.isOpen,
      id: this.params.data.id,
    })
  }

  getGui() {
    return this.eGui
  }
}

class PopupMenuCellRenderer {
  constructor() {
    this.isOpen = false
    this.eMenu = null
    this.tippyInstance = null
  }

  init(params) {
    this.params = params
    this.eGui = document.createElement("div")
    this.eGui.classList.add("py-[15px]")

    const button = document.createElement("button")
    button.type = "button"
    button.classList.add("data-set-control-button")
    button.title = I18n["pop up menu"]

    if (this.params && this.params.value) {
      this.eGui.onclick = (event) => {
        event.preventDefault()
        event.stopPropagation()

        this.togglePopup()
      }

      button.onclick = (event) => {
        event.stopPropagation()
        event.preventDefault()
        this.togglePopup()
      }

      button.innerHTML = `
        <span data-bypass-skip-bug />
        <svg
          class="stroke-current fill-current"
          xmlns="http://www.w3.org/2000/svg"
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
        >
          <path
            d="M12 13C12.5523 13 13 12.5523 13 12C13 11.4477 12.5523 11 12 11C11.4477 11 11 11.4477 11 12C11 12.5523 11.4477 13 12 13Z"
            stroke-width="1.5"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
          <path
            d="M12 6C12.5523 6 13 5.55228 13 5C13 4.44772 12.5523 4 12 4C11.4477 4 11 4.44772 11 5C11 5.55228 11.4477 6 12 6Z"
            stroke-width="1.5"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
          <path
            d="M12 20C12.5523 20 13 19.5523 13 19C13 18.4477 12.5523 18 12 18C11.4477 18 11 18.4477 11 19C11 19.5523 11.4477 20 12 20Z"
            stroke-width="1.5"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      `
    }

    this.tippyInstance = tippy(button)
    this.tippyInstance.disable()

    this.eGui.appendChild(button)
    this.eMenu = this.createMenuComponent()
  }

  togglePopup() {
    this.isOpen = !this.isOpen
    if (this.isOpen) {
      this.configureTippyInstance()
      this.tippyInstance.setContent(this.eMenu)
    } else {
      this.hidePopup()
    }
  }

  hidePopup() {
    this.tippyInstance.unmount()
  }

  configureTippyInstance() {
    this.tippyInstance.enable()
    this.tippyInstance.show()
    this.tippyInstance.setProps({
      trigger: "manual",
      arrow: false,
      interactive: true,
      appendTo: document.body,
      hideOnClick: false,
      theme: "light",
      onShow: (instance) => {
        hideAll({ exclude: instance })
      },
      onClickOutside: (_instance, _event) => {
        this.hidePopup()
      },
    })
  }

  createMenuComponent() {
    const menu = document.createElement("ul")
    menu.classList.add("p-[7.5px]")

    if (this.params && this.params.value) {
      this.params.value.options.forEach(
        ({
          title,
          event: eventName,
          params: eventParams,
          class: classes,
          confirm,
        }) => {
          const option = document.createElement("li")
          option.classList.add("mb-[12px]", "last:mb-[0px]")
          const anchor = document.createElement("a")
          anchor.href = "#"
          anchor.classList.add(
            "block",
            "truncate",
            "text-black",
            "hover:text-gray-700",
            classes,
          )
          anchor.innerText = title

          anchor.onclick = (event) => {
            event.stopPropagation()
            event.preventDefault()

            GenericConfirmDialog.confirm(confirm, () => {
              this.hidePopup()
              this.params.pushEvent(eventName, eventParams)
            })
          }

          option.appendChild(anchor)
          menu.appendChild(option)
        },
      )
    }

    return menu
  }

  getGui() {
    return this.eGui
  }
}

class EngagementRatingCellRenderer {
  init(params) {
    this.eGui = document.createElement("div")
    this.eGui.style.display = "flex"
    this.eGui.style.alignItems = "center"
    this.eGui.style.gap = "4px /* root-scale: legacy-px */"
    this.eGui.style.height = "100%"

    const data = params.data?.engagement_rating
    if (
      !data ||
      data.inactive ||
      data.rating === null ||
      data.rating === undefined
    ) {
      const na = document.createElement("span")
      na.textContent = I18n?.["N/A"] || "N/A"
      na.classList.add("text-gray-400")
      na.style.fontSize = "0.75rem /* root-scale: target */"
      this.eGui.appendChild(na)
      return
    }

    const starClass = this._starClass(data.zone)
    const starTypes = this._starTypes(data.rating)
    const starsEl = document.createElement("div")
    starsEl.style.display = "flex"
    starsEl.style.alignItems = "center"

    starTypes.forEach((type) => {
      const icon = this._starIcon(type, starClass)
      starsEl.appendChild(icon)
    })

    if (data.days_ago_label) {
      const label = document.createElement("span")
      label.textContent = data.days_ago_label
      label.classList.add(...this._labelClass(data.zone).split(" "))
      label.style.fontSize = "0.6875rem /* root-scale: target */"
      label.style.marginLeft = "2px /* root-scale: legacy-px */"
      starsEl.appendChild(label)
    }

    this.eGui.appendChild(starsEl)

    if (data.tooltip_label) {
      const tooltipContent = document.createElement("div")
      tooltipContent.style.padding =
        "4px /* root-scale: legacy-px */ 2px /* root-scale: legacy-px */"
      tooltipContent.textContent = data.tooltip_label
      this.tippyInstance = tippy(this.eGui, {
        content: tooltipContent,
        placement: "top",
        appendTo: document.body,
      })
    }
  }

  _starTypes(rating) {
    const full = Math.floor(rating)
    const hasHalf = rating - full >= 0.5
    const empty = 5 - full - (hasHalf ? 1 : 0)
    return [
      ...Array(full).fill("full"),
      ...(hasHalf ? ["half"] : []),
      ...Array(empty).fill("empty"),
    ]
  }

  _starIcon(type, colorClass) {
    const iconName =
      type === "full"
        ? "mdi:star"
        : type === "half"
          ? "mdi:star-half-full"
          : "mdi:star-outline"
    const icon = document.createElement("div")
    icon.setAttribute("iconify", iconName)
    icon.style.width = "16px /* root-scale: legacy-px */"
    icon.style.height = "16px /* root-scale: legacy-px */"
    icon.classList.add(colorClass)
    return icon
  }

  _starClass(zone) {
    switch (zone) {
      case "healthy":
        return "text-teal-500"
      case "neutral":
        return "text-amber-500"
      case "neglected":
        return "text-red-500"
      default:
        return "text-gray-300"
    }
  }

  _labelClass(zone) {
    switch (zone) {
      case "neutral":
        return "text-amber-500"
      case "neglected":
        return "text-red-500"
      default:
        return "text-gray-500"
    }
  }

  getGui() {
    return this.eGui
  }

  destroy() {
    if (this.tippyInstance) this.tippyInstance.destroy()
  }

  refresh() {
    return false
  }
}

class LastUpdateCellRenderer {
  init(params) {
    const last_update = JSON.parse(params.data.last_update)
    this.eGui = document.createElement("div")
    this.eGui.classList.add("ag-update-comment")
    if (last_update) {
      const fromNow = moment.utc(last_update.inserted_at).fromNow(true)
      this.eGui.innerHTML = `
      <span class="ag-update-comment-time">${fromNow}</span>
      <p class="ag-update-comment-paragraph">${last_update.body_rich_html}</p>
      `
    }
  }

  getGui() {
    return this.eGui
  }

  refresh(params) {
    return true
  }
}

// Renders a JSON-encoded [{name, url}] list (partners, account leads, …) as
// overlapping avatars with a "+N" overflow. The source field is read from the
// colDef so one renderer serves any avatar-list column.
class AvatarsCellRenderer {
  init(params) {
    // Cells are not guaranteed to be the JSON avatar shape (e.g. a same-named
    // column in another grid may hold a plain string), so parse defensively.
    let items = []
    try {
      const parsed = JSON.parse(params.data[params.colDef.field] || "[]")
      if (Array.isArray(parsed)) items = parsed
    } catch {
      items = []
    }
    const amountOfAvatarsToShow = params.amountOfAvatarsToShow || 5
    this.eGui = document.createElement("div")
    this.eGui.classList.add("ag-avatars-wrapper")
    const firstAndLastNameInitials = (name) => {
      const names = String(name || "")
        .trim()
        .split(/\s+/)
      return (names[0]?.[0] || "") + (names[1]?.[0] || "")
    }
    const itemsHTML = items
      .slice(0, amountOfAvatarsToShow)
      .map((p) => {
        // name/url come from user-controlled data — escape before innerHTML.
        const initials = `<span class="ag-avatar-initials">${escapeHtmlText(firstAndLastNameInitials(p.name))}</span>`
        const imageOrInitials = p.url
          ? `${initials}<img src="${escapeHtmlAttr(p.url)}" class="ag-avatar-img rounded-full" width="28" height="28" alt="avatar" onerror="this.style.display='none'">`
          : initials
        return `
          <li class="ag-avatars-item">
            <div class="ag-avatar">
              ${imageOrInitials}
            </div>
          </li>
        `
      })
      .join("")
    const plusSign =
      items.length - amountOfAvatarsToShow <= 0
        ? ""
        : `
      <span class="ag-avatars-text">+${
        items.length - amountOfAvatarsToShow
      }</span>`
    this.eGui.innerHTML = `
      <div class="ag-avatars-inner">
        <ul class="ag-avatars-list">
          ${itemsHTML}
        </ul>
        ${plusSign}
      </div>
    `
  }

  getGui() {
    return this.eGui
  }

  refresh(params) {
    return true
  }
}

class LXDFilter {
  filterValue = ""

  init(params) {
    this.eGui = document.createElement("div")
    this.eGui.classList.add("ag-filter-inner")
    this.filterChangedCallback = params.filterChangedCallback
    this.render()
  }

  render() {
    this.eGui.innerHTML = `<div>
      <label class="flex flex-row gap-[5px] items-center">
          ${I18n["Upcoming in"]}
          <input type="number" min="0" value="${this.filterValue}" pattern="\\d*" autofocus class="ag-input-field-input ag-number-field-input w-[50px]">
          ${I18n["month(s)"]}
        </label>
      </div>`

    const input = this.eGui.querySelector("input")
    input.addEventListener("input", (_e) => {
      if (input.value < 0) {
        input.value = ""
      } else {
        this.setModel(input.value)
        this.filterChangedCallback()
      }
    })
  }

  isFilterActive() {
    return this.filterValue > 0
  }

  getGui() {
    return this.eGui
  }

  getModel() {
    return this.filterValue
  }

  setModel(model) {
    if (model >= 0) {
      this.filterValue = model
    } else {
      this.filterValue = 0
    }
  }

  getModelAsString() {
    return this.filterValue
      ? I18n["in %{count} month(s)"].replace("%{count}", this.filterValue)
      : ""
  }

  doesFilterPass(params) {
    const date = findClosestDate(params.data.lease_expiration)

    return (
      date &&
      date.isAfter(moment()) &&
      !date.isAfter(moment().add(this.filterValue, "months"))
    )
  }
}

function sameSetFilterOptions(a, b) {
  if (a.length !== b.length) return false
  for (let i = 0; i < a.length; i++) {
    if (a[i].value !== b[i].value || a[i].name !== b[i].name) return false
  }
  return true
}

// SetFilter options come from user-controlled fields (e.g. industry strings),
// so escape them before interpolating into innerHTML. `escapeHtmlText` covers
// element text content; `escapeHtmlAttr` also escapes quotes for attributes.
function escapeHtmlText(value) {
  if (value == null) return ""
  return String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
}

function escapeHtmlAttr(value) {
  return escapeHtmlText(value).replace(/"/g, "&quot;").replace(/'/g, "&#39;")
}

class SetFilter {
  options = []
  filterValues = []

  init(params) {
    this.eGui = document.createElement("div")
    this.eGui.classList.add("ag-filter-inner")
    this.filterChangedCallback = params.filterChangedCallback
    // Use the full row model so options aren't narrowed by other active filters
    // (`rowsToDisplay` is post-filter/post-sort) (ENA-9303).
    const rowData = []
    params.api.forEachNode((node) => rowData.push(node.data))
    this.options = this.getOptions(rowData)
    this.render()
  }

  render() {
    this.eGui.innerHTML =
      `<div class="ag-filter-box">
        <ul class="ag-filter-list">` +
      this.options
        .map((option, idx) => {
          const checked = this.filterValues.indexOf(option.value) > -1
          const safeValue = escapeHtmlAttr(option.value)
          const safeName = escapeHtmlText(option.name)
          return `
            <li class="ag-filter-item">
              <div class="checkbox">
                <input type="checkbox" id="checkbox-${idx}" class="checkbox-input" hidden value="${safeValue}" ${checked ? "checked" : ""}>
                <label for="checkbox-${idx}" class="checkbox-label" title="${I18n["Inactive"]}">
                  <span class="checkbox-box">
                    <svg class="checkbox-icon" xmlns="http://www.w3.org/2000/svg" width="12" height="9" viewBox="0 0 12 9" fill="none">
                      <path fill-rule="evenodd" clip-rule="evenodd" d="M10.9179 0.132873C10.7257 -0.0442911 10.4104 -0.0442911 10.2181 0.132873L4.01059 5.8506C3.81824 6.02777 3.50308 6.02777 3.31073 5.8506L1.78182 4.44252C1.58947 4.26536 1.27431 4.26536 1.08196 4.44252L0.144264 5.30665C-0.0480879 5.48381 -0.0480879 5.77408 0.144264 5.95125L3.31073 8.86713C3.50308 9.04429 3.81824 9.04429 4.01059 8.86713L11.8556 1.64114C12.0481 1.46386 12.0481 1.1737 11.8556 0.996419L10.9179 0.132873Z" fill="white"></path>
                    </svg>
                  </span>
                  <span class="checkbox-text">${safeName}</span>
                </label>
              </div>
            </li>`
        })
        .join("") +
      ` </ul>
      </div>`

    this.eGui.querySelectorAll("input").forEach((checkbox) => {
      checkbox.addEventListener("change", (e) => {
        this.filterValues = Array.from(
          this.eGui.querySelectorAll("input:checked"),
          (e) => {
            if (e.value == "null") return null
            else return e.value
          },
        )

        this.filterChangedCallback()
      })
    })
  }

  updateOptions(rowData) {
    const newOptions = this.getOptions(rowData)
    if (!sameSetFilterOptions(this.options, newOptions)) {
      this.options = newOptions
      this.render()
    }
  }

  // Subclasses set `field` to the row-data key to build options from.
  getOptions(rowData) {
    const options = {}
    rowData.forEach((data) => {
      const value = data[this.field]
      options[value] = value == null ? I18n["(Empty)"] : value
    })

    return Object.entries(options)
      .sort()
      .map(([value, name]) => ({ value, name }))
  }

  doesFilterPass(params) {
    if (!this.filterValues) return true
    return this.filterValues.includes(params.data[this.field])
  }

  isFilterActive() {
    return this.filterValues.length > 0
  }

  getGui() {
    return this.eGui
  }

  getModel() {
    return this.filterValues
  }

  setModel(model) {
    this.filterValues = model
  }

  getModelAsString() {
    return this.filterValues.join(", ")
  }
}

class StageFilter extends SetFilter {
  render() {
    if (showProspects()) {
      this.filterValues = this.prospectFilters()
    }

    const options = this.options.map((option) => {
      option.checked = this.filterValues.indexOf(option.value) > -1
      return option
    })
    this.eGui.innerHTML = `
        <div class="ag-filter-box">
          <ul class="ag-filter-list">
            <li class="ag-filter-item">
              <div class="checkbox">
                <input type="checkbox" id="checkbox-0" class="checkbox-input" hidden value="${
                  options[0].value
                }" ${options[0].checked ? "checked" : ""}>
                <label for="checkbox-0" class="checkbox-label" title="${I18n["Inactive"]}">
                  <span class="checkbox-box">
                    <svg class="checkbox-icon" xmlns="http://www.w3.org/2000/svg" width="12" height="9" viewBox="0 0 12 9" fill="none">
                      <path fill-rule="evenodd" clip-rule="evenodd" d="M10.9179 0.132873C10.7257 -0.0442911 10.4104 -0.0442911 10.2181 0.132873L4.01059 5.8506C3.81824 6.02777 3.50308 6.02777 3.31073 5.8506L1.78182 4.44252C1.58947 4.26536 1.27431 4.26536 1.08196 4.44252L0.144264 5.30665C-0.0480879 5.48381 -0.0480879 5.77408 0.144264 5.95125L3.31073 8.86713C3.50308 9.04429 3.81824 9.04429 4.01059 8.86713L11.8556 1.64114C12.0481 1.46386 12.0481 1.1737 11.8556 0.996419L10.9179 0.132873Z" fill="white"></path>
                    </svg>
                  </span>
                  <span class="checkbox-text">${I18n["Inactive"]}</span>
                </label>
              </div>
            </li>
          </ul>
        </div>
        <div class="ag-filter-box">
          <span class="ag-filter-title">${I18n["Prospects"]}</span>
          <ul class="ag-filter-list">
            <li class="ag-filter-item">
              <div class="checkbox">
                <input type="checkbox" id="checkbox-1" class="checkbox-input" hidden value="${
                  options[1].value
                }" ${options[1].checked ? "checked" : ""}>
                <label for="checkbox-1" class="checkbox-label" title="${I18n["Researching"]}">
                  <span class="checkbox-box">
                    <svg class="checkbox-icon" xmlns="http://www.w3.org/2000/svg" width="12" height="9" viewBox="0 0 12 9" fill="none">
                      <path fill-rule="evenodd" clip-rule="evenodd" d="M10.9179 0.132873C10.7257 -0.0442911 10.4104 -0.0442911 10.2181 0.132873L4.01059 5.8506C3.81824 6.02777 3.50308 6.02777 3.31073 5.8506L1.78182 4.44252C1.58947 4.26536 1.27431 4.26536 1.08196 4.44252L0.144264 5.30665C-0.0480879 5.48381 -0.0480879 5.77408 0.144264 5.95125L3.31073 8.86713C3.50308 9.04429 3.81824 9.04429 4.01059 8.86713L11.8556 1.64114C12.0481 1.46386 12.0481 1.1737 11.8556 0.996419L10.9179 0.132873Z" fill="white"></path>
                    </svg>
                  </span>
                  <span class="checkbox-text">${I18n["Researching"]}</span>
                </label>
              </div>
            </li>
            <li class="ag-filter-item">
              <div class="checkbox">
                <input type="checkbox" id="checkbox-2" class="checkbox-input" hidden value="${
                  options[2].value
                }" ${options[2].checked ? "checked" : ""}>
                <label for="checkbox-2" class="checkbox-label" title="${I18n["Active Pursuit"]}">
                  <span class="checkbox-box">
                    <svg class="checkbox-icon" xmlns="http://www.w3.org/2000/svg" width="12" height="9" viewBox="0 0 12 9" fill="none">
                      <path fill-rule="evenodd" clip-rule="evenodd" d="M10.9179 0.132873C10.7257 -0.0442911 10.4104 -0.0442911 10.2181 0.132873L4.01059 5.8506C3.81824 6.02777 3.50308 6.02777 3.31073 5.8506L1.78182 4.44252C1.58947 4.26536 1.27431 4.26536 1.08196 4.44252L0.144264 5.30665C-0.0480879 5.48381 -0.0480879 5.77408 0.144264 5.95125L3.31073 8.86713C3.50308 9.04429 3.81824 9.04429 4.01059 8.86713L11.8556 1.64114C12.0481 1.46386 12.0481 1.1737 11.8556 0.996419L10.9179 0.132873Z" fill="white"></path>
                    </svg>
                  </span>
                  <span class="checkbox-text">${I18n["Active Pursuit"]}</span>
                </label>
              </div>
            </li>
            <li class="ag-filter-item">
              <div class="checkbox">
                <input type="checkbox" id="checkbox-3" class="checkbox-input" hidden value="${
                  options[3].value
                }" ${options[3].checked ? "checked" : ""}>
                <label for="checkbox-3" class="checkbox-label" title="${I18n["Intro Meeting/Discussion"]}">
                  <span class="checkbox-box">
                    <svg class="checkbox-icon" xmlns="http://www.w3.org/2000/svg" width="12" height="9" viewBox="0 0 12 9" fill="none">
                      <path fill-rule="evenodd" clip-rule="evenodd" d="M10.9179 0.132873C10.7257 -0.0442911 10.4104 -0.0442911 10.2181 0.132873L4.01059 5.8506C3.81824 6.02777 3.50308 6.02777 3.31073 5.8506L1.78182 4.44252C1.58947 4.26536 1.27431 4.26536 1.08196 4.44252L0.144264 5.30665C-0.0480879 5.48381 -0.0480879 5.77408 0.144264 5.95125L3.31073 8.86713C3.50308 9.04429 3.81824 9.04429 4.01059 8.86713L11.8556 1.64114C12.0481 1.46386 12.0481 1.1737 11.8556 0.996419L10.9179 0.132873Z" fill="white"></path>
                    </svg>
                  </span>
                  <span class="checkbox-text">${I18n["Intro Meeting/Discussion"]}</span>
                </label>
              </div>
            </li>
            <li class="ag-filter-item">
              <div class="checkbox">
                <input type="checkbox" id="checkbox-4" class="checkbox-input" hidden value="${
                  options[4].value
                }" ${options[4].checked ? "checked" : ""}>
                <label for="checkbox-4" class="checkbox-label" title="${I18n["🔥 High Priority"]}">
                  <span class="checkbox-box">
                    <svg class="checkbox-icon" xmlns="http://www.w3.org/2000/svg" width="12" height="9" viewBox="0 0 12 9" fill="none">
                      <path fill-rule="evenodd" clip-rule="evenodd" d="M10.9179 0.132873C10.7257 -0.0442911 10.4104 -0.0442911 10.2181 0.132873L4.01059 5.8506C3.81824 6.02777 3.50308 6.02777 3.31073 5.8506L1.78182 4.44252C1.58947 4.26536 1.27431 4.26536 1.08196 4.44252L0.144264 5.30665C-0.0480879 5.48381 -0.0480879 5.77408 0.144264 5.95125L3.31073 8.86713C3.50308 9.04429 3.81824 9.04429 4.01059 8.86713L11.8556 1.64114C12.0481 1.46386 12.0481 1.1737 11.8556 0.996419L10.9179 0.132873Z" fill="white"></path>
                    </svg>
                  </span>
                  <span class="checkbox-text">${I18n["🔥 High Priority"]}</span>
                </label>
              </div>
            </li>
          </ul>
        </div>
        <div class="ag-filter-box">
          <span class="ag-filter-title">${I18n["Clients"]}</span>
          <ul class="ag-filter-list">
            <li class="ag-filter-item">
              <div class="checkbox">
                <input type="checkbox" id="checkbox-5" class="checkbox-input" hidden value="${
                  options[5].value
                }" ${options[5].checked ? "checked" : ""}>
                <label for="checkbox-5" class="checkbox-label" title="${I18n["Client"]}">
                  <span class="checkbox-box">
                    <svg class="checkbox-icon" xmlns="http://www.w3.org/2000/svg" width="12" height="9" viewBox="0 0 12 9" fill="none">
                      <path fill-rule="evenodd" clip-rule="evenodd" d="M10.9179 0.132873C10.7257 -0.0442911 10.4104 -0.0442911 10.2181 0.132873L4.01059 5.8506C3.81824 6.02777 3.50308 6.02777 3.31073 5.8506L1.78182 4.44252C1.58947 4.26536 1.27431 4.26536 1.08196 4.44252L0.144264 5.30665C-0.0480879 5.48381 -0.0480879 5.77408 0.144264 5.95125L3.31073 8.86713C3.50308 9.04429 3.81824 9.04429 4.01059 8.86713L11.8556 1.64114C12.0481 1.46386 12.0481 1.1737 11.8556 0.996419L10.9179 0.132873Z" fill="white"></path>
                    </svg>
                  </span>
                  <span class="checkbox-text">${I18n["Client"]}</span>
                </label>
              </div>
            </li>
            <li class="ag-filter-item">
              <div class="checkbox">
                <input type="checkbox" id="checkbox-6" class="checkbox-input" hidden value="${
                  options[6].value
                }" ${options[6].checked ? "checked" : ""}>
                <label for="checkbox-6" class="checkbox-label" title="${I18n["Former Client"]}">
                  <span class="checkbox-box">
                    <svg class="checkbox-icon" xmlns="http://www.w3.org/2000/svg" width="12" height="9" viewBox="0 0 12 9" fill="none">
                      <path fill-rule="evenodd" clip-rule="evenodd" d="M10.9179 0.132873C10.7257 -0.0442911 10.4104 -0.0442911 10.2181 0.132873L4.01059 5.8506C3.81824 6.02777 3.50308 6.02777 3.31073 5.8506L1.78182 4.44252C1.58947 4.26536 1.27431 4.26536 1.08196 4.44252L0.144264 5.30665C-0.0480879 5.48381 -0.0480879 5.77408 0.144264 5.95125L3.31073 8.86713C3.50308 9.04429 3.81824 9.04429 4.01059 8.86713L11.8556 1.64114C12.0481 1.46386 12.0481 1.1737 11.8556 0.996419L10.9179 0.132873Z" fill="white"></path>
                    </svg>
                  </span>
                  <span class="checkbox-text">${I18n["Former Client"]}</span>
                </label>
              </div>
            </li>
          </ul>
        </div>
      `

    this.eGui.querySelectorAll("input").forEach((checkbox) => {
      checkbox.addEventListener("change", (e) => {
        this.filterValues = Array.from(
          this.eGui.querySelectorAll("input:checked"),
          (e) => {
            if (e.value == "null") return null
            else return e.value
          },
        )

        this.filterChangedCallback()
      })
    })
  }
  doesFilterPass(params) {
    // Pass all items if filter values haven't been initialized
    if (!this.filterValues) return true
    return this.filterValues.includes(params.data.stage)
  }

  prospectFilters() {
    return [
      "Researching",
      "Active Pursuit",
      "Intro Meeting/Discussion",
      "🔥 High Priority",
    ]
  }

  getOptions() {
    return [
      { value: "Inactive", name: I18n["Inactive"] },
      { value: "Researching", name: I18n["Researching"] },
      { value: "Active Pursuit", name: I18n["Active Pursuit"] },
      {
        value: "Intro Meeting/Discussion",
        name: I18n["Intro Meeting/Discussion"],
      },
      { value: "🔥 High Priority", name: I18n["🔥 High Priority"] },
      { value: "Client", name: I18n["Client"] },
      { value: "Former Client", name: I18n["Former Client"] },
    ]
  }
}

class ProductTypeFilter extends SetFilter {
  field = "product_type"
}

class IndustryFilter extends SetFilter {
  field = "industry"
}

// account_lead_avatars holds a JSON-encoded [{name, url}] list (an account can
// have several leads), so options and matching work off every lead name rather
// than a single cell value — letting a user filter the grid down to one or more
// selected leads, the same multi-select behaviour as the other SetFilters. The
// field is deliberately distinct from the Teams grids' string-valued
// `account_leads` so the JSON avatar renderer/filter never touch that column (ENA-9551).
class AccountLeadsFilter extends SetFilter {
  field = "account_lead_avatars"

  leadNames(data) {
    try {
      return JSON.parse(data?.account_lead_avatars || "[]")
        .map((lead) => lead.name)
        .filter(Boolean)
    } catch {
      return []
    }
  }

  getOptions(rowData) {
    const options = {}
    rowData.forEach((data) => {
      this.leadNames(data).forEach((name) => {
        options[name] = name
      })
    })
    return Object.entries(options)
      .sort()
      .map(([value, name]) => ({ value, name }))
  }

  doesFilterPass(params) {
    if (!this.filterValues || this.filterValues.length === 0) return true
    const names = this.leadNames(params.data)
    return this.filterValues.some((value) => names.includes(value))
  }
}

export const AgGrid = {
  applyRowState(data, api, representationType) {
    if (!data || data.trim() === "") {
      console.warn("[GRID] Row state data is empty, skipping applyRowState")
      return
    }

    let expandedList
    try {
      expandedList = JSON.parse(data)
    } catch (error) {
      console.warn("[GRID] Failed to parse row state data:", data, error)
      return
    }

    if (!expandedList || !Array.isArray(expandedList)) {
      console.warn("[GRID] expandedList is null or not an array:", expandedList)
      return
    }

    api.forEachNode((rowNode) => {
      if (expandedList.includes(rowNode.data.id)) {
        rowNode.setExpanded(true)
      }

      rowNode.addEventListener("expandedChanged", (event) => {
        pushEvent(this, "location:expand", {
          account_id: rowNode.data.id,
          expanded: rowNode.expanded,
          representation_type: representationType,
        })
      })
    })
  },

  gridOptions: {},

  initGrid() {
    refreshI18n(this.el)

    const grid =
      this.el.querySelector("#ag-grid-grid") ||
      this.el.querySelector(".ag-grid-grid")

    this.columnDefsStr = this.el.dataset.columnDefs
    this.rowDataStr = this.el.dataset.rowData
    this.metadata = JSON.parse(this.el.dataset.metadata)

    const quickfilterSelector =
      this.metadata?.quickfilterSelector || "#ag-grid-quickfilter"
    const quickfilterBox = document.querySelector(quickfilterSelector)
    if (quickfilterBox) {
      quickfilterBox.addEventListener("input", (e) => {
        this.api.setGridOption("quickFilterText", e.target.value)
      })
    }

    this.rowClickCallback = (event) => {
      document.dispatchEvent(new Event("click", { bubble: true }))
    }

    this.gridOptions = {
      theme: themeBalham,
      masterDetail: true,
      detailRowAutoHeight: true,
      isRowMaster: (dataItem) => {
        return (
          dataItem.representation_type === "occupier" &&
          dataItem.locations_count > 0
        )
      },
      detailCellRendererParams: {
        detailGridOptions: {
          columnDefs: buildLocationColumnDefs(),
          defaultColDef: {
            flex: 1,
            sortable: true,
            resizable: true,
          },
          suppressLoadingOverlay: true,
        },
        getDetailRowData: (params) => {
          // Ensure locations data exists before calling successCallback
          const locations = params.data?.locations || []
          params.successCallback(locations)
        },
      },
      rowHeight: 50,
      alwaysShowVerticalScroll: true,
      suppressSetFilterByDefault: true,
      suppressAnimationFrame: false,
      rowBuffer: 30, // Bigger buffer for smoother scrolling
      asyncTransactionWaitMillis: 100,
      suppressMenuHide: this.metadata?.suppressMenuHide || false,
      columnHoverHighlight: this.metadata?.columnSeparator || false,
      components: {
        agDateInput: CustomDateComponent,
        agDateRangeFilter: DateRangeFilter,
        // Merge in any custom components from extending hooks
        ...(this.getCustomComponents ? this.getCustomComponents() : {}),
      },
      defaultColDef: {
        filter: this.metadata?.defaultColDef?.filter || false,
        resizable:
          this.metadata?.defaultColDef?.resizable !== undefined
            ? this.metadata.defaultColDef.resizable
            : true,
        sortable:
          this.metadata?.defaultColDef?.sortable !== undefined
            ? this.metadata.defaultColDef.sortable
            : true,
        editable: false,
        floatingFilter: this.metadata?.defaultColDef?.floatingFilter || false,
        menuTabs: this.metadata?.defaultColDef?.menuTabs || [],
        cellStyle: { display: "flex", alignItems: "center" },
      },
      dataTypeDefinitions: {
        dateString: {
          filter: true,
          baseDataType: "dateString",
          extendsDataType: "dateString",
          valueFormatter: (params) => {
            if (!params.value) return ""

            const [year, month, date] = params.value.split("-")
            return `${month}/${date}/${year}`
          },
          dateParser: (value) => {
            if (value == null || value === "") {
              return undefined
            }
            const dateParts = value.split("-")
            return dateParts.length === 3
              ? new Date(
                  parseInt(dateParts[0]),
                  parseInt(dateParts[1]) - 1,
                  parseInt(dateParts[2]),
                )
              : undefined
          },
          dateFormatter: (value) => {
            if (value == null) {
              return undefined
            }
            const date = String(value.getDate())
            const month = String(value.getMonth() + 1)
            return `${value.getFullYear()}-${
              month.length === 1 ? "0" + month : month
            }-${date.length === 1 ? "0" + date : date}`
          },
        },
      },
      rowSelection: rowSelection(this),
      selectionColumnDef: {
        cellStyle: { display: "flex", alignItems: "center" },
      },
      onSelectionChanged: (e) => {
        if (this._correctingSelection) return

        const selectedNodes = this.api.getSelectedNodes()
        const displayedCount = this.api.getDisplayedRowCount()

        if (selectedNodes.length > displayedCount) {
          const displayedIds = new Set()
          for (let i = 0; i < displayedCount; i++) {
            const node = this.api.getDisplayedRowAtIndex(i)
            if (node) displayedIds.add(node.id)
          }

          const hiddenSelected = selectedNodes.filter(
            (node) => !displayedIds.has(node.id),
          )

          if (hiddenSelected.length > 0) {
            this._correctingSelection = true
            hiddenSelected.forEach((node) => node.setSelected(false))
            this._correctingSelection = false
          }
        }

        pushRowSelection(this, this.api.getSelectedRows())
      },
      columnDefs: parseColumnDefs(
        this.el.dataset.columnDefs,
        this.metadata,
        this,
      ),
      rowData: this.rowData || [],
      colResizeDefault: "shift",
      columnTypes: {
        lxdsColumn: lxdsColumnType,
        pastRedDatesColumn: pastRedDatesColumnType,
      },
      localeText: {
        ...I18n,
        dateFormatOoo: "mm/dd/yyyy",
      },
      overlayNoRowsTemplate: "<span></span>",
      onRowClicked: (event) => {
        const path = event.eventPath ?? event.composedPath?.() ?? []
        const cell = path.find(
          (node) => node instanceof Element && node.hasAttribute("col-id"),
        )

        if (cell?.getAttribute("col-id") == "ag-Grid-SelectionColumn") {
          event.node.setSelected(!event.node.isSelected())
        } else {
          if (event.data["partner?"] === false) {
            pushEvent(this, "show-not-partner-modal", {
              account_alias: event.data.account_alias,
            })
          } else {
            pushEvent(this, "click-draggable", {
              draggableId: event.data.partner_alias ?? event.data.account_alias,
            })
          }
        }
      },
      onFilterChanged: (event) => {
        event.api?.refreshCells({ force: true, columns: ["address"] })

        const allRows = this.el.querySelectorAll(".ag-row")
        allRows.forEach((row) => {
          if (!row.hasBeenDisabled) {
            row.addEventListener("click", this.rowClickCallback)
          }
        })
      },
      onColumnMoved: (gridOptions) => {
        if (gridOptions.finished) {
          updateColumnState(this, gridOptions)

          let columnDefs = this.api.getColumnDefs()
          columnDefs = applyCellStyling(columnDefs)
          columnDefs = setLastColumnUndraggable(columnDefs)
          this.api.updateGridOptions({ columnDefs: columnDefs })
        }
      },
      onColumnResized: (gridOptions) => {
        if (
          gridOptions.finished &&
          gridOptions.source !== "flex" &&
          gridOptions.source !== "api"
        ) {
          updateColumnState(this, gridOptions)
        }
      },
      onSortChanged: (gridOptions) => {
        if (gridOptions.source !== "api") {
          updateColumnState(this, gridOptions)
        }
      },
      onGridReady: (gridOptions) => {
        applyColumnState(this.el.dataset.columnState, gridOptions.api)
        this.applyRowState(
          this.el.dataset.rowState,
          gridOptions.api,
          this.el.dataset.representationType,
        )
      },
      onRowDataUpdated: (gridOptions) => {
        // Repopulate dynamic SetFilter options (industry, product_type) once data
        // arrives. getColumnFilterInstance is async in AG Grid 35.3 — await it,
        // otherwise the refresh no-ops and the popup stays empty (ENA-9326).
        const rowData = []
        gridOptions.api.forEachNode((node) => rowData.push(node.data))

        const columnDefs = gridOptions.api.getColumnDefs()
        columnDefs.forEach((colDef) => {
          if (!colDef.field) return
          gridOptions.api
            .getColumnFilterInstance(colDef.field)
            .then((filterInstance) => {
              if (
                filterInstance &&
                typeof filterInstance.updateOptions === "function"
              ) {
                filterInstance.updateOptions(rowData)
              }
            })
            .catch((error) =>
              console.warn(
                `[GRID] Failed to refresh filter options for ${colDef.field}`,
                error,
              ),
            )
        })
      },
      getRowId: (params) =>
        params.data.partner_alias ??
        params.data.account_alias ??
        `${params.data.id}`,
      getRowClass: (params) => {
        if (params.data["partner?"] !== undefined && !params.data["partner?"]) {
          return "ag-row-non-partner"
        }
      },
      sideBar:
        this.metadata?.sideBar === false
          ? false
          : {
              toolPanels: [
                {
                  id: "columns",
                  labelDefault: I18n["Columns"],
                  labelKey: "columns",
                  iconKey: "columns",
                  toolPanel: "agColumnsToolPanel",
                  toolPanelParams: {
                    suppressRowGroups: true,
                    suppressValues: true,
                    suppressPivots: true,
                    suppressPivotMode: true,
                    suppressColumnFilter: true,
                    suppressColumnSelectAll: true,
                    suppressColumnExpandAll: true,
                  },
                },
              ],
            },
    }

    // Set initial row data if available (for progressive loading first chunk)
    if (this.rowData) {
      this.gridOptions.rowData = this.rowData
    }

    this.api = createGrid(grid, this.gridOptions)

    // Fix duplicate headers when columns change with detail rows
    this.api.addEventListener("columnVisible", (event) => {
      // Collapse all expanded detail rows, then force complete re-render
      const expandedNodes = []
      this.api.forEachNode((node) => {
        if (node.master && node.expanded) {
          expandedNodes.push(node)
          node.setExpanded(false)
        }
      })

      // Force complete grid refresh to clear stale detail grid DOM
      setTimeout(() => {
        this.api.resetRowHeights()
        this.api.redrawRows()

        // Re-expand rows after cleanup (optional - comment out if not desired)
        // expandedNodes.forEach(node => node.setExpanded(true))
      }, 0)
    })

    // Process any pending responses that arrived before the API was ready
    if (this.pendingResponses && this.pendingResponses.length > 0) {
      const responses = this.pendingResponses
      this.pendingResponses = []
      responses.forEach((response) => this.handleChunkResponse(response))
    }

    this.el.addEventListener("ag-grid:csv:export", (event) => {
      this.api.exportDataAsCsv({
        fileName: `${I18n["all_accounts"]}.csv`,
        processCellCallback: (cell) => {
          if (
            cell.column.colId == "partners" ||
            cell.column.colId == "account_lead_avatars"
          ) {
            return JSON.parse(cell.value || "[]")
              .map((person) => sanitizeCSV(person.name))
              .join(", ")
          } else if (cell.column.colId == "lease_expiration") {
            const dates = cell.value
              .map((date) => getShortDateFormatter().format(new Date(date)))
              .join(", ")

            return dates
          } else if (cell.column.colId == "last_update") {
            const update = JSON.parse(cell.value)

            if (update) {
              const tempDiv = document.createElement("div")
              tempDiv.innerHTML = update.body_rich_html
              const textContent = tempDiv.textContent || ""
              const fromNow = moment.utc(update.inserted_at).fromNow(true)

              return `${fromNow} ago:\n${textContent}`
            } else {
              return ""
            }
          } else if (cell.column.colId == "engagement_rating") {
            // valueFormatter is bypassed when a processCellCallback is present,
            // so format the raw object here too (otherwise "[object Object]").
            return formatEngagementRating(cell.value)
          } else {
            return sanitizeCSV(cell.value)
          }
        },
      })
    })

    // We add this to prevent row click event from triggering when clicking outside
    // a filter popup
    this.el.querySelectorAll(".ag-floating-filter-button").forEach((elm) => {
      elm.addEventListener("click", (e) => {
        e.stopImmediatePropagation()
        const agPopup = this.el.querySelector(".ag-popup")
        const allRows = this.el.querySelectorAll(".ag-row")

        const clickOutsideCallback = (event) => {
          const isClickInside = agPopup.contains(event.target)
          if (!isClickInside) {
            document.removeEventListener("click", clickOutsideCallback)
            this.el.querySelectorAll(".ag-row").forEach((row) => {
              row.removeEventListener("click", this.rowClickCallback)
            })
          }
        }
        document.addEventListener("click", clickOutsideCallback)
        allRows.forEach((row) => {
          row.hasBeenDisabled = true
          row.addEventListener("click", this.rowClickCallback)
        })
      })
    })

    if (showProspects()) {
      this.api.getColumnFilterInstance("stage").then((filterInstance) => {
        filterInstance.filterChangedCallback()
      })

      setTimeout(() => {
        document.querySelector("button.ag-filter-active")?.click()
      })
    }
  },

  updateGridData() {
    // Immediately clear any persistent loading states and reset chunk tracking
    this.chunksReceived = 0
    this.allRowData = []
    this.allRowDataById = {}
    this.clearLoadingState()
    this.currentCallbackId = null // Track current callback to prevent stale updates

    // Cancel any pending operations when switching tabs
    if (this.api) {
      // Stop any ongoing async transactions
      this.api.flushAsyncTransactions()
    }

    // Check if we have data in the data attribute (backward compatibility)
    const rowDataStr = this.el.dataset.rowData

    if (rowDataStr && rowDataStr.trim() !== "") {
      if (this.api) {
        // Use updateRowData with REMOVE for all rows - most efficient method
        this.api.setGridOption("rowData", [])
        this.api.setGridOption("loading", true)
      }
      // Use existing data from attribute (legacy behavior)
      const rowData = parseRowData(rowDataStr)
      if (this.api) {
        setTimeout(() => {
          this.api.setGridOption("rowData", rowData)
          this.api.setGridOption("loading", false)
          this.clearLoadingState()
        }, 0)
      } else {
        this.rowData = rowData
        this.initGrid()
      }
    } else {
      // Progressive chunk loading path
      if (this.api) {
        this.api.setGridOption("rowData", []) // Instant clear without iteration
        this.api.setGridOption("loading", true)
      } else {
        this.rowData = []
        this.initGrid()
        // Set loading after grid is initialized
        setTimeout(() => {
          if (this.api) {
            this.api.setGridOption("loading", true)
          }
        }, 10)
      }

      // Request data (will receive multiple chunk responses)
      const callbackId = `callback_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
      const representationType = this.el.dataset.representationType
      this.currentCallbackId = callbackId

      this.pushEvent("ag-grid-get-rows", {
        representationType: representationType,
        callbackId: callbackId,
      })
    }
  },

  // Helper method to aggressively clear loading state
  clearLoadingState() {
    // Clear AG Grid loading overlay
    if (this.api) {
      this.api.setGridOption("loading", false)
    }

    // Aggressively clear aria-live loading announcements - multiple selectors for safety
    const ariaContainers = this.el.querySelectorAll(
      ".ag-aria-description-container, [aria-live]",
    )

    ariaContainers.forEach((container) => {
      container.textContent = ""
      container.innerHTML = ""
      container.setAttribute("aria-live", "off")
      // Force DOM update
      container.style.display = "none"
      setTimeout(() => {
        container.style.display = ""
        // Double-check it's really empty
        container.textContent = ""
      }, 0)
    })
  },

  handleChunkResponse(response) {
    // If API is not ready yet, queue the response for later processing
    if (!this.api) {
      this.pendingResponses = this.pendingResponses || []
      this.pendingResponses.push(response)
      return
    }

    // Ignore stale chunks from previous requests
    if (
      this.currentCallbackId &&
      response.callbackId !== this.currentCallbackId
    ) {
      return
    }

    // Accumulate all row data
    if (response.isFirstChunk) {
      this.chunksReceived = 1
      this.allRowData = [...response.rowData]
    } else {
      this.chunksReceived++
      this.allRowData = [...this.allRowData, ...response.rowData]
    }

    // Update grid with accumulated data using setGridOption for consistency
    // This prevents duplicate headers that occur with incremental transactions
    if (this.api) {
      this.api.setGridOption("rowData", this.allRowData)
      this.allRowData.forEach((r) => (this.allRowDataById[r.id] = r))

      // Scroll to top only on first chunk - delay until rows are rendered
      if (response.isFirstChunk) {
        setTimeout(() => {
          if (this.api && this.allRowData.length > 0) {
            this.api.ensureIndexVisible(0, "top")
          }
        }, 0)
      }

      // Clear loading state for multi-chunk scenarios
      if (response.isFirstChunk && !response.isLastChunk) {
        this.clearLoadingState()
      }
    }

    // Clear loading overlay when batch is complete
    if (response.isLastChunk) {
      // Add a small delay for single chunks to ensure AG Grid has processed the data
      if (response.isFirstChunk && response.isLastChunk) {
        // Single chunk scenario - AG Grid keeps re-adding the loading text
        // We need to clear it multiple times to combat AG Grid's internal state

        // First clear immediately
        this.clearLoadingState()

        // Clear once more after AG Grid has fully processed
        setTimeout(() => {
          this.clearLoadingState()

          // Final aggressive clear - directly target the container
          const ariaContainer = this.el.querySelector(
            ".ag-aria-description-container",
          )
          if (ariaContainer && ariaContainer.textContent.includes("Loading")) {
            ariaContainer.textContent = ""
            ariaContainer.innerHTML = ""
            // Remove the element entirely and let AG Grid recreate it if needed
            ariaContainer.remove()
          }
        }, 300)
      } else {
        // Multi-chunk scenario - can clear immediately
        this.clearLoadingState()
      }
    }
  },

  mounted() {
    // Initialize row data tracking for change detection
    this.rowDataStr = this.el.dataset.rowData

    // Initialize progressive chunk loading state
    this.chunksReceived = 0
    this.allRowData = []
    this.allRowDataById = {}

    this.handleEvent("ag_grid_response", (response) => {
      // Check if response matches current representation type
      const currentRepType = this.el.dataset.representationType
      if (
        response.representationType &&
        response.representationType !== currentRepType
      ) {
        return
      }

      this.handleChunkResponse(response)
    })

    this.handleEvent("ag-grid:rows:remove", ({ ids }) => {
      const rowsToRemove = ids.map((id) => this.allRowDataById[id])
      this.api.applyTransaction({ remove: rowsToRemove })
      this.allRowData = this.allRowData.filter((row) => !ids.includes(row.id))
    })

    this.handleEvent("broker_card_created", (params) => {
      const transaction = this.api.applyTransaction({
        add: [params.broker_card],
      })

      const [rowNode] = transaction.add
      if (rowNode != undefined) {
        this.api.ensureIndexVisible(rowNode.rowIndex, "middle")
      }

      this.api
        .getColumnFilterInstance("product_type")
        .then((instance) =>
          instance.updateOptions(
            this.gridOptions.rowData.concat([rowNode.data]),
          ),
        )
    })

    this.handleEvent("broker_card_removed", (params) => {
      const transaction = this.api.applyTransaction({
        remove: [params.broker_card],
      })
    })

    this.handleEvent("ag-grid:deselect-all", (_params) => {
      this.api.deselectAll("all")
    })

    this.handleEvent("ag-grid-update-rows-and-columns", (_params) => {
      this.updateGridData()
    })

    this.handleEvent("ag-grid-update-row", (params) => {
      if (this.api && params.row) {
        const getRowId = this.api.getGridOption("getRowId")
        const rowId = getRowId?.({ data: params.row, level: 0 })
        if (!rowId || this.api.getRowNode(rowId)) {
          this.api.applyTransaction({ update: [params.row] })
        }
      }
    })

    LicenseManager.setLicenseKey(this.el.dataset.licenseKey)

    // Load data and initialize grid
    this.updateGridData()
  },

  updated() {
    // Check if row data attribute changed
    const rowDataChanged = this.el.dataset.rowData !== this.rowDataStr

    // Handle row data changes (backward compatibility)
    if (rowDataChanged && this.api) {
      this.rowDataStr = this.el.dataset.rowData
      this.updateGridData()
      return
    }

    if (this.el.dataset.columnDefs != this.columnDefsStr) {
      const metadata = JSON.parse(this.el.dataset.metadata)
      this.columnDefsStr = this.el.dataset.columnDefs
      const columnDefs = parseColumnDefs(
        this.el.dataset.columnDefs,
        metadata,
        this,
      )

      this.api.updateGridOptions({ columnDefs: columnDefs })
    }

    pushRowSelection(this, this.api.getSelectedRows())

    // sync quick filter box and grid-options when liveview is updated
    const quickfilterSelector =
      this.metadata?.quickfilterSelector || "#ag-grid-quickfilter"
    const quickFilterText = document.querySelector(quickfilterSelector)?.value
    this.api.setGridOption("quickFilterText", quickFilterText)

    const columnState = this.el.dataset.columnState
    applyColumnState(columnState, this.api)
    this.applyRowState(
      this.el.dataset.rowState,
      this.api,
      this.el.dataset.representationType,
    )
  },

  destroyed() {},
}

const lxdsColumnType = {
  filter: LXDFilter,
  valueFormatter: (params) => {
    const cellDate = findClosestDate(params.value)
    if (!cellDate) return null

    const formattedCellDate = cellDate.format("MM/DD/YYYY")
    const lxdCount = params.value.length

    if (lxdCount > 1) {
      return `${formattedCellDate} +${lxdCount - 1}`
    }

    return formattedCellDate
  },
}

const pastRedDatesColumnType = {
  cellStyle: (params) => {
    const base = { display: "flex", alignItems: "center" }
    const closestDate = findClosestDate(params.value)

    if (!closestDate) {
      return base
    }

    const userTimeZone = document.querySelector("body").dataset.userTimeZone
    const closestDateString = closestDate.format("YYYY-MM-DD")
    const dateString = dateWithTZ(closestDateString, userTimeZone)
    const cellDate = moment(dateString, "YYYY-MM-DD HH:mm:ss ZZ")
    const now = moment()
    if (cellDate && cellDate.isBefore(now)) {
      return { ...base, color: "red" }
    }

    return base
  },
}

function findClosestDate(dateStrings) {
  // Check if the list is empty
  if (!dateStrings || dateStrings.length === 0) {
    return null
  }

  // Parse the date strings using moment
  const dates = dateStrings.map((dateStr) => momentFromString(dateStr))
  const now = moment()

  // Find the date closest to now in the future
  const closestFutureDate = dates
    .filter((date) => date.isAfter(now))
    .reduce((prev, curr) => {
      if (!prev) return curr

      return curr.diff(now, "seconds") < prev.diff(now, "seconds") ? curr : prev
    }, null)

  // If there's a future date, return it
  if (closestFutureDate) {
    return closestFutureDate
  }

  // Otherwise, find the date closest to now in the past
  const closestPastDate = dates
    .filter((date) => date.isBefore(now))
    .reduce((prev, curr) => {
      if (!prev) return curr

      return now.diff(curr, "seconds") < now.diff(prev, "seconds") ? curr : prev
    }, null)

  return closestPastDate
}

function momentFromString(dateString) {
  if (dateString) return moment(dateString, "YYYY-MM-DD")
  return null
}

function parseColumnDefs(string, metadata, viewHook) {
  let columnDefs = JSON.parse(string)

  columnDefs = columnDefs.map((columnDef) => {
    if (columnDef.field == "commission") {
      columnDef.field = "commissionValue"
    }
    return columnDef
  })
  columnDefs = applyCustomSorting(columnDefs)
  columnDefs = addFilters(columnDefs, metadata?.filters)
  columnDefs = addRenderers(columnDefs, viewHook, metadata?.renderers)
  columnDefs = addLocationAddressMatching(columnDefs)
  columnDefs = addFormatters(columnDefs)
  columnDefs = applyCellStyling(columnDefs)
  columnDefs = setLastColumnUndraggable(columnDefs)

  return columnDefs
}

// Returns column group map - function ensures I18n is loaded before access
function getColumnGroupMap() {
  return {
    // Company Details
    name: I18n["Company Details"],
    stage: I18n["Company Details"],
    industry: I18n["Company Details"],
    partners: I18n["Company Details"],
    teams: I18n["Company Details"],

    // Location Details
    address: I18n["Location Details"],
    city: I18n["Location Details"],
    state: I18n["Location Details"],
    zip: I18n["Location Details"],
    size: I18n["Location Details"],
    lease_expiration: I18n["Location Details"],
    product_type: I18n["Location Details"],
    locations_count: I18n["Location Details"],

    // Contact Details
    contact_name: I18n["Contact Details"],
    contact_email: I18n["Contact Details"],
    contact_phone_number: I18n["Contact Details"],

    // Deal Details
    active_deals: I18n["Deal Details"],
    closed_deals: I18n["Deal Details"],

    // CRM Details
    inserted_at: I18n["CRM Details"],
    notes: I18n["CRM Details"],
    last_sales_activity_date: I18n["CRM Details"],
    last_update: I18n["CRM Details"],
    sales_activities: I18n["CRM Details"],
  }
}

function columnGroup(column) {
  const columnGroupMap = getColumnGroupMap()
  return columnGroupMap[column.field] || I18n["Other"]
}

function groupColumns(columnDefs) {
  // Check if any columns have defined groups (not just "Other")
  const hasDefinedGroups = columnDefs.some(
    (column) => columnGroupMap[column.field] !== undefined,
  )

  // If no columns have defined groups, return ungrouped
  if (!hasDefinedGroups) {
    return columnDefs
  }

  const grouped = columnDefs.reduce((acc, column) => {
    acc[columnGroup(column)] = acc[columnGroup(column)] || {
      headerName: columnGroup(column),
      children: [],
    }

    acc[columnGroup(column)].children.push(column)
    return acc
  }, {})

  return Object.values(grouped)
}

function parseRowData(string) {
  const parsed = JSON.parse(string)
  const mapped = parsed.map((row) => {
    let sizeUnit = null
    let commissionValue = null
    if (row.formatted_size != null) {
      const splitRow = row.formatted_size.split(" ")
      sizeUnit = splitRow[1]
    }

    if (row.commission) {
      commissionValue = parseFloat(
        row.commission.substring(1).replaceAll(",", ""),
      )
    }

    return {
      sizeUnit: sizeUnit,
      commissionValue: commissionValue,
      ...row,
    }
  })
  return mapped
}

function rowSelection(hook) {
  if (hook.metadata.bulkActions) {
    return { mode: "multiRow", selectAll: "filtered" }
  } else {
    return false
  }
}

function pushRowSelection(hook, selectedRows) {
  const rows = selectedRows.map((row) => {
    return {
      partner_id: row.id,
      account_id: row.account_id,
      admin: row.admin,
      lead: row.lead,
    }
  })

  pushEvent(hook, "selection-changed", { selected_rows: rows })
}

function setLastColumnUndraggable(columnDefs) {
  return columnDefs.map((col, idx) => {
    if (idx == columnDefs.length - 1) {
      col.resizable = false
    } else {
      col.resizable = true
    }
    return col
  })
}

function updateColumnState(hook, gridOptions) {
  const columnState = gridOptions.api.getColumnState()
  const event = "save-column-state"
  const params = { element_id: hook.el.id, columnState: columnState }

  pushEvent(hook, event, params)
}

function applyCustomSorting(columnDefs) {
  function stageComparator(valueA, valueB, nodeA, nodeB, isInverted) {
    const stagesInOrder = [
      "inactive",
      "researching",
      "active pursuit",
      "intro meeting",
      "high priority",
      "client",
    ]
    const valueAStageIndex = stagesInOrder.findIndex((value) => {
      const regex = new RegExp(value, "i")
      return valueA.match(regex)
    })
    const valueBStageIndex = stagesInOrder.findIndex((value) => {
      const regex = new RegExp(value, "i")
      return valueB.match(regex)
    })
    return valueAStageIndex - valueBStageIndex
  }
  function sizeComparator(valueA, valueB, nodeA, nodeB, isInverted) {
    if (!valueA) {
      return 1
    }
    if (!valueB) {
      return -1
    }
    const unitsA = nodeA.data.sizeUnit
    const unitsB = nodeB.data.sizeUnit
    if (unitsA === unitsB) {
      return valueA > valueB ? 1 : -1
    } else {
      if (unitsA == "sf") return -1
      else return 1
    }
  }

  function dateComparator(valueA, valueB, nodeA, nodeB, isInverted) {
    // Since we're showing all LXDs now
    // we might receive many dates
    // we want the last position
    if (Array.isArray(valueA)) {
      valueA = valueA[valueA.length - 1]
    }

    if (Array.isArray(valueB)) {
      valueB = valueB[valueB.length - 1]
    }

    if (!valueA) {
      return 1
    }
    if (!valueB) {
      return -1
    }
    valueA = Date.parse(valueA)
    valueB = Date.parse(valueB)
    return valueA > valueB ? 1 : -1
  }

  function addressComparator(valueA, valueB, nodeA, nodeB, isInverted) {
    if (!valueA) {
      return 1
    }
    if (!valueB) {
      return -1
    }
    let numberA = parseInt(valueA.split(" ")[0])
    const restA = valueA.split(" ").slice(1).join(" ")
    numberA = isNaN(numberA) ? 99999 : numberA
    let numberB = parseInt(valueB.split(" ")[0])
    const restB = valueB.split(" ").slice(1).join(" ")
    numberB = isNaN(numberB) ? 99999 : numberB
    if (numberA === numberB) {
      return restA > restB ? 1 : -1
    } else {
      return numberA > numberB ? 1 : -1
    }
  }

  function lastUpdateComparator(valueA, valueB, nodeA, nodeB, isInverted) {
    if (!valueA) {
      return 1
    }
    if (!valueB) {
      return -1
    }
    let parsedA = JSON.parse(valueA)
    let parsedB = JSON.parse(valueB)

    const dateA = new Date(parsedA.inserted_at)
    const dateB = new Date(parsedB.inserted_at)
    return dateA < dateB ? 1 : -1
  }

  function caseInsensitiveComparator(valueA, valueB, nodeA, nodeB, isInverted) {
    if (typeof valueA === "string") {
      return valueA.toLowerCase().localeCompare(valueB.toLowerCase())
    }

    return valueA > valueB ? 1 : valueA < valueB ? -1 : 0
  }

  return columnDefs.map((col) => {
    if (col.field === "stage") {
      col.comparator = stageComparator
    }
    if (col.field === "size") {
      col.comparator = sizeComparator
    }
    if (col.field === "lease_expiration") {
      col.comparator = dateComparator
    }
    if (col.field === "address" || col.headerName === "Building") {
      col.comparator = addressComparator
    }
    if (col.field === "last_update") {
      col.comparator = lastUpdateComparator
    }
    if (col.field === "name") {
      col.comparator = caseInsensitiveComparator
    }
    if (col.field === "last_sales_activity_date") {
      col.comparator = dateComparator
    }
    return col
  })
}

function addFilters(columnDefs, metadata) {
  return columnDefs.map((col) => {
    if (metadata?.stageFilter && col.field === "stage") {
      col.filter = StageFilter
    }
    if (col.field === "product_type") {
      col.filter = ProductTypeFilter
    }
    if (col.field === "industry") {
      col.filter = IndustryFilter
    }
    if (col.field === "account_lead_avatars") {
      col.filter = AccountLeadsFilter
    }
    if (col.field === "size") {
      col.filter = "agNumberColumnFilter"
      col.filterParams = {
        filterOptions: [
          "empty",
          "equals",
          "lessThanOrEqual",
          "greaterThanOrEqual",
          {
            displayKey: "between",
            displayName: I18n["Between"],
            predicate: ([fv1, fv2], cellValue) =>
              fv1 <= cellValue && fv2 >= cellValue,
            numberOfInputs: 2,
          },
          "blank",
          "notBlank",
        ],
        defaultOption: "equals",
      }
    }
    if (col.field === "last_sales_activity_date") {
      col.filter = "agDateColumnFilter"
      col.filterParams = dateColumnFilterParams
    }
    if (col.field === "inserted_at") {
      col.filter = "agDateColumnFilter"
      col.filterParams = dateColumnFilterParams
    }
    if (col.field === "engagement_rating") {
      // Filter by floored star count. Inactive / N/A rows are surfaced as
      // null so they fall out of every numeric comparison.
      col.filterValueGetter = (params) => {
        const data = params.data?.engagement_rating
        if (!data || data.inactive || data.rating == null) return null
        return Math.floor(data.rating)
      }
      col.filter = "agNumberColumnFilter"
      col.filterParams = {
        filterOptions: [
          "equals",
          "lessThanOrEqual",
          "greaterThanOrEqual",
          {
            displayKey: "between",
            displayName: I18n["Between"],
            predicate: ([fv1, fv2], cellValue) => {
              if (cellValue == null) return false
              return fv1 <= cellValue && fv2 >= cellValue
            },
            numberOfInputs: 2,
          },
          "blank",
          "notBlank",
        ],
        defaultOption: "greaterThanOrEqual",
        buttons: ["reset", "apply"],
        closeOnApply: true,
      }
    }
    return col
  })
}

const ISO_DATE_RE = /^(\d{4})-(\d{2})-(\d{2})/
const dateColumnFilterParams = {
  filterOptions: ["lessThan", "greaterThan", "inRange"],
  defaultOption: "lessThan",
  // Include the start/end days in the range filter (AG Grid defaults this to
  // false, which would drop accounts created exactly on a boundary date).
  inRangeInclusive: true,
  buttons: ["reset", "apply"],
  closeOnApply: true,
  comparator: (filterDate, cellValue) => {
    const cellMidnight = parseIsoDateAsLocalMidnight(cellValue)
    if (cellMidnight === null) return -1
    if (cellMidnight < filterDate) return -1
    if (cellMidnight > filterDate) return 1
    return 0
  },
}

// Build the date at LOCAL midnight; `new Date("YYYY-MM-DD")` parses as UTC and
// shifts the calendar day for users west of UTC.
function parseIsoDateAsLocalMidnight(cellValue) {
  if (cellValue == null || cellValue === "") return null
  const match = ISO_DATE_RE.exec(String(cellValue))
  if (!match) return null
  const year = Number(match[1])
  const month = Number(match[2])
  const day = Number(match[3])
  if (!year || !month || !day) return null
  return new Date(year, month - 1, day)
}

function addRenderers(columnDefs, viewHook, metadata) {
  return columnDefs.map((col) => {
    if (col.field === "partners" || col.field === "account_lead_avatars") {
      col.cellRenderer = AvatarsCellRenderer
      col.cellRendererParams = {
        amountOfAvatarsToShow: metadata?.amountOfAvatarsToShow,
      }
    } else if (col.field === "last_update") {
      col.cellRenderer = LastUpdateCellRenderer
    } else if (col.field === "popup_menu") {
      // Value is an object driven entirely by the custom renderer; opt out of
      // v36 cell-data-type inference so it isn't typed as 'object' (warning #48).
      col.cellDataType = false
      col.cellRenderer = PopupMenuCellRenderer
      col.cellRendererParams = {
        pushEvent: (event, params) => pushEvent(viewHook, event, params),
      }
    } else if (col.field === "include") {
      col.cellRenderer = CheckboxCellRenderer
      col.cellRendererParams = {
        pushEvent: (event, params) => pushEvent(viewHook, event, params),
      }
    } else if (col.field === "engagement_rating") {
      // Value is an object surfaced via the custom renderer/comparator/filter;
      // opt out of v36 cell-data-type inference so it isn't typed as 'object'
      // (warning #48 — the inferred 'object' type has no default formatter).
      col.cellDataType = false
      col.cellRenderer = EngagementRatingCellRenderer
      col.comparator = (valueA, valueB, nodeA, nodeB, isDescending) => {
        const a = nodeA.data?.engagement_rating
        const b = nodeB.data?.engagement_rating
        const aBottom =
          !a || a.inactive || a.days_ago === null || a.days_ago === undefined
        const bBottom =
          !b || b.inactive || b.days_ago === null || b.days_ago === undefined
        if (aBottom && bBottom) return 0
        if (aBottom) return isDescending ? -1 : 1
        if (bBottom) return isDescending ? 1 : -1
        // Ascending = least engaged first = highest days_ago first
        return b.days_ago - a.days_ago
      }
    }

    return col
  })
}

function addLocationAddressMatching(columnDefs) {
  return columnDefs.map((col) => {
    if (col.field !== "address") return col

    col.getQuickFilterText = (params) =>
      locationSearchText(params.data, params.value)

    col.filterParams = {
      ...(col.filterParams || {}),
      textMatcher: locationTextMatcher,
    }

    col.cellRenderer = (params) => renderMatchedAddress(params)

    return col
  })
}

function locationSearchText(data, fallback) {
  const locations = Array.isArray(data?.locations) ? data.locations : []
  if (locations.length <= 1) return fallback ?? ""
  return locations.map(locationAddressText).join(" ")
}

function locationAddressText(loc) {
  return [loc.display_address, loc.street, loc.city, loc.state]
    .filter(Boolean)
    .join(" ")
}

// Return a DOM node with textContent, never an HTML string: a string cellRenderer
// is injected via innerHTML, and the address can contain a free-text place name.
function renderMatchedAddress(params) {
  const el = document.createElement("span")
  el.textContent = matchedAddressText(params)
  return el
}

function matchedAddressText(params) {
  const fallback = params.value == null ? "" : String(params.value)
  const locations = Array.isArray(params.data?.locations)
    ? params.data.locations
    : []
  if (locations.length <= 1) return fallback

  const matchers = activeAddressMatchers(params.api)
  if (matchers.length === 0) return fallback

  const matchIndex = locations.findIndex((loc) =>
    matchers.some((matches) => matches(loc)),
  )
  if (matchIndex <= 0) return fallback

  const matched = locations[matchIndex]
  const matchedAddress = matched.display_address || locationAddressText(matched)
  return `${locations.length - 1}+ ${matchedAddress}`
}

// One predicate per active source — the quick filter and the Address column
// filter (whose AND/OR operator is honored) — kept separate so a location is
// shown when it satisfies either on its own, not the flattened union of both.
function activeAddressMatchers(api) {
  const matchers = []

  const quickTerms = []
  tokenize(api?.getGridOption?.("quickFilterText") ?? "", quickTerms)
  if (quickTerms.length > 0) {
    matchers.push((loc) => containsEveryTerm(loc, quickTerms))
  }

  const addressMatcher = filterModelMatcher(api?.getFilterModel?.()?.address)
  if (addressMatcher) matchers.push(addressMatcher)

  return matchers
}

function filterModelMatcher(model) {
  if (!model) return null
  if (Array.isArray(model.conditions)) {
    const subs = model.conditions.map(filterModelMatcher).filter(Boolean)
    if (subs.length === 0) return null
    return model.operator === "OR"
      ? (loc) => subs.some((m) => m(loc))
      : (loc) => subs.every((m) => m(loc))
  }
  if (typeof model.filter !== "string" || model.filter.trim() === "")
    return null
  const needle = model.filter.trim().toLowerCase()
  return (loc) =>
    textOperatorMatches(
      model.type,
      locationAddressText(loc).toLowerCase(),
      needle,
    )
}

function textOperatorMatches(type, haystack, needle) {
  switch (type) {
    case "equals":
      return haystack === needle
    case "notEqual":
      return haystack !== needle
    case "startsWith":
      return haystack.startsWith(needle)
    case "endsWith":
      return haystack.endsWith(needle)
    case "notContains":
      return !haystack.includes(needle)
    case "blank":
      return haystack.trim() === ""
    case "notBlank":
      return haystack.trim() !== ""
    default:
      return haystack.includes(needle)
  }
}

// Evaluate the Address text filter against each location independently rather
// than one concatenated string, so equals/startsWith/endsWith can match a
// secondary location. Positive operators pass if ANY location matches; negative
// operators require EVERY location to satisfy them. AG Grid lower-cases value
// and filterText already; locationAddressText is lower-cased here to match.
function locationTextMatcher(params) {
  const { filterOption, filterText, value, node } = params
  const needle = filterText == null ? "" : String(filterText)
  const locations = Array.isArray(node?.data?.locations)
    ? node.data.locations
    : []

  if (locations.length <= 1) {
    return textOperatorMatches(
      filterOption,
      value == null ? "" : String(value),
      needle,
    )
  }

  const test = (loc) =>
    textOperatorMatches(
      filterOption,
      locationAddressText(loc).toLowerCase(),
      needle,
    )
  // Operators that include the row only when EVERY location satisfies them:
  // negations (notContains/notEqual) and blank (a row is blank only if all are).
  const requiresEvery =
    filterOption === "notContains" ||
    filterOption === "notEqual" ||
    filterOption === "blank"
  return requiresEvery ? locations.every(test) : locations.some(test)
}

function tokenize(value, terms) {
  const trimmed = String(value).trim().toLowerCase()
  if (trimmed) terms.push(...trimmed.split(/\s+/))
}

function containsEveryTerm(loc, terms) {
  const haystack = locationAddressText(loc).toLowerCase()
  return terms.every((term) => haystack.includes(term))
}

function addFormatters(columnDefs) {
  return columnDefs.map((col) => {
    if (col.field === "size") {
      col.valueFormatter = (params) => params.node.data.formatted_size
    } else if (col.field === "commissionValue") {
      col.valueFormatter = (params) => params.node.data.commission
    } else if (col.field === "teams") {
      col.valueFormatter = (params) => {
        if (params.data && Array.isArray(params.data.teams)) {
          return params.data.teams.join(", ")
        }
        return ""
      }
    } else if (col.field === "last_sales_activity_date") {
      // Use raw ISO date for sorting, formatted date for display
      col.valueGetter = (params) => params.data.last_sales_activity_date_raw
      col.valueFormatter = (params) => params.data.last_sales_activity_date
    } else if (col.field === "inserted_at") {
      // Locale-aware short date (mm/dd/yyyy for US accounts).
      col.valueFormatter = (params) => {
        const date = parseIsoDateAsLocalMidnight(params.value)
        return date === null ? "" : getShortDateFormatter().format(date)
      }
    } else if (col.field === "engagement_rating") {
      // The cell value is an object ({rating, zone, days_ago_label, ...}), so
      // AG Grid's data-type inference needs a valueFormatter to derive display
      // text (#48). EngagementRatingCellRenderer draws the on-screen stars; this
      // produces the equivalent text for clipboard copy and CSV/Excel export.
      col.valueFormatter = (params) =>
        formatEngagementRating(params.data?.engagement_rating)
    }
    return col
  })
}

function applyCellStyling(columnDefs) {
  return columnDefs.map((col, idx) => {
    if (idx == 0) {
      col.cellStyle = { display: "flex", alignItems: "center", fontWeight: 600 }
    } else {
      delete col.cellStyle
    }
    return col
  })
}

function dateWithTZ(date, timeZone) {
  const offset = getOffset(timeZone)
  let fourDigitOffset = (Math.abs(offset) * 100).toString().padStart(4, "0")
  fourDigitOffset = offset >= 0 ? `+${fourDigitOffset}` : `-${fourDigitOffset}`
  return `${date} 23:59:59 ${fourDigitOffset}`
}

function getOffset(timeZone = "UTC", date = new Date()) {
  const utcDate = new Date(date.toLocaleString("en-US", { timeZone: "UTC" }))
  const tzDate = new Date(date.toLocaleString("en-US", { timeZone }))
  return (tzDate.getTime() - utcDate.getTime()) / 6e4 / 60
}

class CustomDateComponent {
  init(params) {
    const template = `
          <input type="text" data-input style="width: 100%;" placeholder="${I18n["mm/dd/yyyy"]}" />
          <a class="input-button" title="${I18n["Clear"]}" data-clear></a>`

    this.params = params

    this.eGui = document.createElement("div")
    this.eGui.setAttribute("role", "presentation")
    this.eGui.classList.add("ag-input-wrapper")
    this.eGui.classList.add("ag-date-filter")
    this.eGui.innerHTML = template

    this.eInput = this.eGui.querySelector("input")
    this.eInput.classList.add("ag-input-field-input", "ag-text-field-input")

    this.picker = flatpickr(this.eGui, {
      dateFormat: "m/d/Y",
      locale: flatpickrLocale(),
      wrap: true,
      onChange: this.onDateChanged.bind(this),
      onReady: (_selectedDates, _dateStr, instance) =>
        markAgGridDatePickerPopup(instance),
    })

    this.date = null
  }

  getGui() {
    return this.eGui
  }

  onDateChanged(selectedDates) {
    this.date = selectedDates[0] || null
    this.params.onDateChanged()
  }

  getDate() {
    return this.date
  }

  setDate(date) {
    this.picker.setDate(date)
    this.date = date
  }

  setInputPlaceholder(placeholder) {
    this.eInput.setAttribute("placeholder", placeholder)
  }
}

class DateRangeFilter {
  destroy() {
    this.flatpickrFrom.destroy()
    this.flatpickrTo.destroy()
  }

  doesFilterPass(params) {
    const locations = params.data.locations

    if (!locations || locations.length === 0) {
      return false
    }

    let datesAfterFrom = null
    if (this.dateFrom) {
      datesAfterFrom = new Set(
        locations.filter((location) => {
          return new Date(location.expires_at) >= this.dateFrom
        }),
      )
    }

    let datesBeforeTo = null
    if (this.dateTo) {
      datesBeforeTo = new Set(
        locations.filter((location) => {
          return new Date(location.expires_at) <= this.dateTo
        }),
      )
    }

    if (datesAfterFrom && datesBeforeTo) {
      return datesAfterFrom.intersection(datesBeforeTo).size != 0
    } else if (datesAfterFrom) {
      return datesAfterFrom.size != 0
    } else if (datesBeforeTo) {
      return datesBeforeTo.size != 0
    } else {
      return false
    }
  }

  getGui() {
    return this.eGui
  }

  getModel() {
    if (!this.isFilterActive()) {
      return null
    }

    return {
      dateFrom: this.dateFrom
        ? this.dateFrom.toISOString().substring(0, 10)
        : null,
      dateTo: this.dateTo ? this.dateTo.toISOString().substring(0, 10) : null,
    }
  }

  getModelAsString() {
    const formattedDateFrom =
      this.dateFrom && `From: ${getShortDateFormatter().format(this.dateFrom)}`
    const formattedDateTo =
      this.dateTo && `To: ${getShortDateFormatter().format(this.dateTo)}`

    return [formattedDateFrom, formattedDateTo]
      .filter((d) => d != null)
      .join("-")
  }

  init(params) {
    this.params = params
    this.dateFrom = null
    this.dateTo = null
    this.getValue = params.getValue

    const inputClasses =
      "p-[5px] border border-gray-200 outline-hidden focus:border-blue-500 rounded-[2.5px] bg-white"
    const buttonClasses =
      "cursor-pointer p-[5px] bg-blue-400 text-white rounded-[2.5px]"

    this.eGui = document.createElement("div")
    this.eGui.classList.add("date-filter-popup")
    this.eGui.innerHTML = `
    <div class="flex flex-col gap-[7.5px] p-[10px] bg-gray-100">
      <h4 class="font-medium">${I18n["Date Range"]}</h4>
      <input type="text" id="dateFrom" class="date-from-input ${inputClasses}" placeholder="${I18n["Start date..."]}" />
      <input type="text" id="dateTo" class="date-to-input ${inputClasses}" placeholder="${I18n["End date..."]}" />
      <button class="reset-button ${buttonClasses}">${I18n["Reset filter"]}</button>
    </div>
    `

    this.dateFromInput = this.eGui.querySelector(".date-from-input")
    this.dateToInput = this.eGui.querySelector(".date-to-input")
    this.resetButton = this.eGui.querySelector(".reset-button")

    this.dateFromInput.classList.add(
      "ag-input-field-input",
      "ag-text-field-input",
    )

    this.flatpickrFrom = flatpickr(this.dateFromInput, {
      dateFormat: "m/d/Y",
      locale: flatpickrLocale(),
      wrap: false,
      onChange: ([selectedDate]) => {
        this.dateFrom = selectedDate
        this.params.filterChangedCallback()
      },
    })

    this.flatpickrTo = flatpickr(this.dateToInput, {
      dateFormat: "m/d/Y",
      locale: flatpickrLocale(),
      wrap: false,
      onChange: ([selectedDate]) => {
        this.dateTo = selectedDate
        this.params.filterChangedCallback()
      },
    })

    this.resetButton.addEventListener("click", () => {
      this.flatpickrFrom.clear()
      this.flatpickrTo.clear()
    })
  }

  isFilterActive() {
    return !!this.dateFrom || !!this.dateTo
  }

  setModel(model) {
    if (model) {
      this.dateFrom = model.dateFrom ? new Date(model.dateFrom) : null
      this.dateTo = model.dateTo ? new Date(model.dateTo) : null

      this.flatpickrFrom.setDate(this.dateFrom, false)
      this.flatpickrTo.setDate(this.dateTo, false)
    } else {
      this.flatpickrFrom.clear()
      this.flatpickrTo.clear()
    }
  }
}

// Renders the engagement_rating object ({rating, days_ago_label, ...}) as the
// text equivalent of the on-screen stars. rating is already in 0.5 increments
// (ProspectEngagement), so it is emitted verbatim to preserve half-stars in
// clipboard copy and CSV/Excel export. Shared by the column valueFormatter and
// the accounts CSV processCellCallback (which bypasses valueFormatter).
function formatEngagementRating(data) {
  if (!data || data.inactive || data.rating == null) {
    return I18n?.["N/A"] || "N/A"
  }
  return data.days_ago_label
    ? `${data.rating} (${data.days_ago_label})`
    : `${data.rating}`
}

function sanitizeCSV(data) {
  if (Array.isArray(data)) {
    return data.map(sanitizeCSV)
  } else if (/^[\=\+\-\@\t\r]/.test(data)) {
    return "'" + data
  } else {
    return data
  }
}

window.moment = moment
// trigger rebuild
